import { Fragment, FunctionComponent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { DraftDesignConfiguration } from '@/types';
import { trackFeature } from '@/utils/adobeLaunch';
import { renderTemplateString, t } from '@/utils/i18n';
import { useConfigSwitch } from '@/hooks/useConfigSwitch';
import { useMasksAndFiltersAvailable } from '@/hooks/useMasksAndFiltersAvailable';
import { isPixelDesign } from '@/model/design/isDesignType';
import { isUploadedDesign } from '@/model/design/isUploadedDesign';
import { getIsScreenPrintType } from '@/model/printType/getIsScreenPrintType';
import { toggleDesignFavoriteAction } from '@/redux/designs/actions/toggleDesignFavoriteAction';
import { getDesignFavorites } from '@/redux/designs/getters/getDesignFavorites';
import { StoreDispatch } from '@/redux/types';
import { TextLink } from '@/components/common/TextLink';
import { useFocus } from '@/components/common/focus';
import { CaretRight } from '@/components/common/icons/Caret';
import { Edit } from '@/components/common/icons/Edit';
import { Star } from '@/components/common/icons/Star';
import { Panel, PanelInfo } from '@/components/common/panel/panel';
import { ColorSelector } from '../ColorSelector';
import { ConfigurationControls } from '../ConfigurationControls';
import { PrintTypePanel, PrintTypeSelectorButton } from '../PrintTypePanel';
import { StickerPanelAction, StickerSizePanel } from '../product-info/StickerPanel';
import { AsyncImageEditDialog } from './AsyncImageEditDialog';
import { PrintAreaPanel } from './PrintAreaPanel';
import './DesignConfigurationPanel.scss';

interface Props {
  configuration: DraftDesignConfiguration;
}

enum SubPanels {
  None,
  PrintType,
  PrintArea,
  ImageEdit,
}

export const DesignConfigurationPanel: FunctionComponent<Props> = ({ configuration }) => {
  const dispatch = useDispatch<StoreDispatch>();
  const [activeSubPanel, setActiveSubPanel] = useState(SubPanels.None);
  const design = configuration.design;
  const favoritedDesignIds = useSelector(getDesignFavorites);
  const isFavorited = favoritedDesignIds.includes(design.id);
  const [isMaskAvailable, isFilterAvailable] = useMasksAndFiltersAvailable(configuration);
  const [settingsPanel, focusSettingsPanel] = useFocus();
  const isUpload = isUploadedDesign(configuration.design);

  useConfigSwitch(configuration, focusSettingsPanel);

  function handleBackClick() {
    setActiveSubPanel(SubPanels.None);
  }

  function toggleFavorite() {
    if (!isFavorited) {
      trackFeature('design-favorite-add', { designId: design.id }, true);
    }
    dispatch(toggleDesignFavoriteAction(design.id));
  }

  return (
    <Fragment>
      <Panel
        hidden={activeSubPanel !== SubPanels.None}
        className="design-configuration"
        onEntered={focusSettingsPanel}
        ref={settingsPanel}
      >
        <div className="design-configuration__header">
          <div className="design-configuration__header-info">
            <h3 className="design-configuration__design-name ellipsis">{design.name}</h3>
            <p className="design-configuration__design-id">ID: {design.id}</p>
          </div>
          {!isUploadedDesign(design) && (
            <button
              type="button"
              title={t(isFavorited ? 'favorites.remove' : 'favorites.add', design.name)}
              className={cn('design-configuration__favorite', {
                'design-configuration__favorite--active': isFavorited,
              })}
              onClick={toggleFavorite}
            >
              {isFavorited ? (
                <Star
                  type="filled"
                  className="design-configuration__favorite-icon"
                  width="1.75em"
                  height="1.75em"
                />
              ) : (
                <Star
                  type="default"
                  className="design-configuration__favorite-icon"
                  width="1.75em"
                  height="1.75em"
                />
              )}
            </button>
          )}
        </div>
        <PrintTypeSelectorButton
          printType={configuration.printType}
          onClick={() => setActiveSubPanel(SubPanels.PrintType)}
        />
        <ColorSelector key={configuration.cid} configuration={configuration} />
        {isUpload && (
          <button
            type="button"
            onClick={() => setActiveSubPanel(SubPanels.ImageEdit)}
            className="design-configuration__image-edit-button"
            disabled={!isMaskAvailable || !isFilterAvailable}
          >
            <Edit className="design-configuration__image-edit-icon" />
            <span className="design-configuration__image-edit-label">{t('image.edit.title')}</span>
            <CaretRight />
          </button>
        )}
        {isUpload &&
          !isMaskAvailable &&
          !isFilterAvailable &&
          !getIsScreenPrintType(configuration.printType) && (
            <PanelInfo>{t('error.photoSize')}</PanelInfo>
          )}
        {getIsScreenPrintType(configuration.printType) && isPixelDesign(design) && (
          <PanelInfo>
            {renderTemplateString('image.warning.transformPixel', (text) => (
              <TextLink key="href" href={t('contact.linkHref')}>
                {text}
              </TextLink>
            ))}
          </PanelInfo>
        )}
        <ConfigurationControls
          configuration={configuration}
          onPaSelectorClick={() => setActiveSubPanel(SubPanels.PrintArea)}
        />
        <StickerPanelAction
          configuration={configuration}
          trackDesignSizingPanel={() => trackFeature('design-sizing-panel-open')}
        />
      </Panel>
      <StickerSizePanel configuration={configuration} />
      <PrintTypePanel
        active={activeSubPanel === SubPanels.PrintType}
        parent={t('designSettings.label')}
        configuration={configuration}
        onBackClick={handleBackClick}
      />
      <PrintAreaPanel
        active={activeSubPanel === SubPanels.PrintArea}
        configuration={configuration}
        onClose={handleBackClick}
      />
      {(isMaskAvailable || isFilterAvailable) && (
        <AsyncImageEditDialog
          configuration={configuration}
          open={activeSubPanel === SubPanels.ImageEdit}
          onClose={handleBackClick}
          isMaskAvailable={isMaskAvailable}
          isFilterAvailable={isFilterAvailable}
        />
      )}
    </Fragment>
  );
};
